// src/App.js
import TopNavBar from "examples/Navbars/TopNavBar";
import React, { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Routes & Contexts
import routes from "routes";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { AuthProvider } from "context/AuthContext";
//import { attachTokenInterceptor } from "lib/axios";
import RootGate from "layouts/RootGate";

// Branding images
import brandWhite from "assets/images/cropped-Chatbot-Icon.png";
import brandDark from "assets/images/cropped-Chatbot-Icon.png";

// Layout components
//import Sidenav from "examples/Sidenav";
import PortalRedirect from "layouts/PortalRedirect";

// MUI theming
// Themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
//import "assets/styles/fonts.css";

// Date‐picker provider
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export default function App() {
  // ====== UI Controller & Sidenav state ======
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor, darkMode } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // ====== Authentication routes (no sidenav) ======
  const authRoutes = [
    "/authentication/sign-in",
    "/authentication/sign-up",
    "/registration",
    "/authentication/reset-password",
    "/profile",
  ];
  const isAuthRoute = authRoutes.includes(pathname);

  // ====== RTL cache setup ======
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // ====== Sidenav hover behavior ======
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // ====== Direction & scroll-to-top ======
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [pathname]);

  // ====== Recursively render your routes.js config ======
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (route.route)
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      return null;
    });

  // ====== The core app content ======
  const AppContent = (
    <>
      <Routes>
        {/* with basename="/portal", index === "/portal" */}
        <Route index element={<RootGate />} />
        {getRoutes(routes)}
        <Route path="*" element={<RootGate />} />
      </Routes>
    </>
  );

  // ====== Wrap in LocalizationProvider for MUI Date Pickers ======
  const WrappedContent = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>{AppContent}</LocalizationProvider>
  );

  // ====== Final render, with RTL vs LTR theming ======
  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <AuthProvider>
        <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
          <CssBaseline />
          {WrappedContent}
        </ThemeProvider>
      </AuthProvider>
    </CacheProvider>
  ) : (
    <AuthProvider>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        {WrappedContent}
      </ThemeProvider>
    </AuthProvider>
  );
}
